import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { useEmailMarketing } from '../../EmailMarketingProvider';
import { CampaignDraft, EmailLocation } from '../../types';
import { Panel } from '../../UI/Panel/Panel';
import { StatusPill } from '../../UI/StatusPill/StatusPill';
import { LocationsChecklist } from '../../UI/LocationsChecklist/LocationsChecklist';
import { SENDER_STATUS } from '../../Sender/senderStatus';
import SenderSetupModal from '../../Sender/SenderSetupModal';
import StepHeading from './StepHeading';
import styles from './steps.module.scss';

// Keep the alert compact: list up to 3 locations, the rest behind "View more".
const BLOCKED_PREVIEW = 3;

interface LocationsStepProps {
    draft: CampaignDraft;
    onChange: (patch: Partial<CampaignDraft>) => void;
}

// Step 1: the campaign is created per location, so locations come first.
// Locations without a verified sender are blocked (same rule as the main app).
export const LocationsStep: React.FC<LocationsStepProps> = ({ draft, onChange }) => {
    const { locations, senderFor, isSenderReady, saveSenderEmail } = useEmailMarketing();
    const [settingUp, setSettingUp] = useState<EmailLocation | null>(null);
    const [showAllBlocked, setShowAllBlocked] = useState(false);

    const blocked = locations.filter(location => !isSenderReady(location.id));
    const visibleBlocked = showAllBlocked ? blocked : blocked.slice(0, BLOCKED_PREVIEW);
    const hiddenCount = blocked.length - BLOCKED_PREVIEW;

    return (
        <>
            <StepHeading
                title="Choose the locations"
                description="Each selected location sends this campaign to its own customers, from its own email."
            />

            <Panel title="Campaign">
                <Input
                    label="Campaign name"
                    placeholder="e.g. Summer Spa Week 2026"
                    value={draft.name}
                    maxLength={100}
                    onChange={event => onChange({ name: event.target.value })}
                />
                <p className={styles.fieldHint}>Only your team sees this name.</p>
            </Panel>

            <Panel
                title="Locations"
                description={`${draft.locationIds.length} selected`}
            >
                <div className={styles.locationsBox}>
                    <LocationsChecklist
                        locations={locations}
                        selectedIds={draft.locationIds}
                        listClassName={styles.locationsList}
                        isDisabled={location => !isSenderReady(location.id)}
                        renderMeta={location => {
                            const sender = senderFor(location.id);
                            if (sender?.status === 'verified') {
                                return <span className={styles.senderEmail}>{sender.email}</span>;
                            }
                            const status = SENDER_STATUS[sender?.status ?? 'unconfigured'];
                            return (
                                <StatusPill
                                    label={status.label}
                                    tone={status.tone}
                                />
                            );
                        }}
                        onChange={ids => onChange({ locationIds: ids })}
                    />
                </div>

                {blocked.length > 0 && (
                    <div className={styles.blocked}>
                        <FontAwesomeIcon icon={faCircleInfo} />
                        <div>
                            <strong>
                                {blocked.length} location{blocked.length === 1 ? '' : 's'} can&apos;t be included yet
                            </strong>
                            <p>They need a verified sender email first.</p>
                            <ul>
                                {visibleBlocked.map(location => (
                                    <li key={location.id}>
                                        <span>{location.name}</span>
                                        <button
                                            type="button"
                                            className={styles.linkButton}
                                            onClick={() => setSettingUp(location)}
                                        >
                                            {senderFor(location.id)?.email ? 'Fix sender' : 'Set up sender'}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            {hiddenCount > 0 && (
                                <button
                                    type="button"
                                    className={`${styles.linkButton} ${styles.viewMore}`}
                                    onClick={() => setShowAllBlocked(prev => !prev)}
                                >
                                    {showAllBlocked ? 'View less' : `View ${hiddenCount} more`}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </Panel>

            {settingUp && (
                <SenderSetupModal
                    currentEmail={senderFor(settingUp.id)?.email ?? null}
                    locationName={settingUp.name}
                    onClose={() => setSettingUp(null)}
                    onSave={email => saveSenderEmail(settingUp.id, email)}
                />
            )}
        </>
    );
};

export default LocationsStep;
